import { useEffect, useRef, useState } from 'react';
import type { CompanionManifest, CompanionState } from '@/lib/companion/types';
import { readCompanionActivity } from '@/lib/companion/companion-activity';
import { isCapturingNow } from '@/lib/recordings/use-is-capturing';
import type { CompanionController, ControlOwner } from '../types/controller';
import type { FrameStore } from '../types/frame';
import {
  chooseMove, pauseDwellMs, pickStrollTarget, LONG_IDLE_MS, type Rng,
} from '../utils/dwell';
import { pickAmbient, AMBIENT_STATE } from '../utils/ambient';
import { daypartAt } from '../utils/daypart';
import {
  measureSurfaces, overlapsX, resolveSurface, restY, FEET_DROP_PX, SURFACE_EPS,
  type Ledge, type Wall,
} from '../utils/surfaces';
import { routeHome } from '../utils/route-home';
import { readWorld, watchLayout, COMPANION_SLOTS } from '../utils/measure-dom';
import {
  clampDt, stepFall, stepJump, stepWalk, walkSpeedPxS,
  DROP_SETTLE_MS, PAUSE_POLL_MS,
} from '../utils/physics';
import { decideReaction } from '../utils/reactions';
import { exposeCompanionDebug } from '../utils/debug';

type Phase = 'pause' | 'walk' | 'fall' | 'jump' | 'transition';

/** How high above the standing ledge a hop may reach, in multiples of sprite
 *  height — hops downward are unrestricted since gravity handles the landing. */
const MAX_HOP_RISE_FACTOR = 1.5;

/** How often a hop picks the favoured perch over any other reachable ledge. */
const FAVOURED_HOP_CHANCE = 0.8;

/** How often a beat commits to the route home rather than idling. High enough
 *  that the companion reliably arrives, low enough that the journey still
 *  reads as wandering rather than marching. */
const HOMEWARD_COMMIT = 0.65;

/** How long the tab must be hidden before returning counts as coming back
 *  rather than glancing away. */
export const AWAY_MS = 120_000;

/** Module-level so the default is referentially stable. As an inline default
 *  it was a fresh closure every render, and since the clock is an effect
 *  dependency that tore down and rebuilt the whole engine on each ownership
 *  change — losing which app events had already been reacted to. */
const systemClock = () => new Date();

export interface EngineOptions {
  manifest: CompanionManifest;
  frames: FrameStore;
  enabled: boolean;
  displayWidth: number;
  displayHeight: number;
  rng?: Rng;
  now?: () => Date;
}

export function useCompanionEngine({
  manifest, frames, enabled, displayWidth, displayHeight,
  rng = Math.random, now: wallClock = systemClock,
}: EngineOptions): CompanionController {
  const [owner, setOwner] = useState<ControlOwner>('autonomous');
  const ownerRef = useRef<ControlOwner>('autonomous');
  const controllerRef = useRef<CompanionController | null>(null);
  const neutralizeRef = useRef<(() => void) | null>(null);
  const settleTimerRef = useRef(0);

  const clearSettleTimer = () => {
    if (settleTimerRef.current !== 0) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = 0;
    }
  };

  const transitionToRef = useRef<((via: CompanionState, target: CompanionState) => void) | null>(null);
  const isGroundedRef = useRef<((x: number, y: number) => boolean) | null>(null);

  if (controllerRef.current === null) {
    controllerRef.current = {
      setState: (state) => frames.set({ state }),
      play: (animation) => frames.set({ state: animation as CompanionState }),
      moveTo: () => undefined,
      react: () => {
        if (frames.get().state === 'sleeping') {
          transitionToRef.current?.('wakingUp', 'idle');
          return;
        }
        frames.set({ state: 'reacting' });
      },
      claim: (next) => {
        clearSettleTimer();
        ownerRef.current = next;
        setOwner(next);
        neutralizeRef.current?.();
      },
      release: (who) => {
        if (ownerRef.current !== who) return;
        ownerRef.current = 'autonomous';
        setOwner('autonomous');
        if (frames.get().state === 'sleeping') transitionToRef.current?.('wakingUp', 'idle');
      },
      settleAfterDrop: () => {
        if (ownerRef.current !== 'interaction') return;
        settleTimerRef.current = window.setTimeout(() => {
          settleTimerRef.current = 0;
          ownerRef.current = 'autonomous';
          setOwner('autonomous');
        }, DROP_SETTLE_MS);
      },
      transitionTo: (via, target) => transitionToRef.current?.(via, target),
      isGrounded: (x, y) => isGroundedRef.current?.(x, y) ?? false,
      get owner() { return ownerRef.current; },
    };
  }

  useEffect(() => {
    if (!enabled) return;

    let rafId = 0;
    let timerId = 0;
    let stopped = false;

    // Starting in 'pause' with an already-elapsed deadline routes the first
    // frame through plan(), which is what actually resolves whether the
    // companion is airborne (and should fall/jump) or already resting.
    let phase: Phase = 'pause';
    let last = performance.now();
    let pauseUntil = -Infinity;
    // Set by the layout observer, consumed by the pause phase. The ledge the
    // companion is standing on may have just stopped existing.
    let layoutDirty = false;
    let targetX = 0;
    let targetLedge: Ledge | null = null;
    // The ledge the last plan resolved the companion onto. Held across beats
    // so the walk phase knows which edges are its own rather than barriers.
    let standingLedge: Ledge | null = null;
    let fallVelocity = 0;
    let jumpFrom = 0;
    let jumpElapsed = 0;
    let transitionTarget: CompanionState | null = null;
    let idleSince = last;
    let asleep = false;
    let seenEdgeAt = 0;
    let lastReactionAt = -Infinity;
    let walls: Wall[] = [];
    // Held alongside `walls` so phases between planning beats can ask what is
    // under the companion. Gravity has to be answerable every tick, not only
    // when a dwell expires.
    let ledges: Ledge[] = [];

    const speed = walkSpeedPxS(displayWidth, manifest.loopMs);

    const clearScheduled = () => {
      if (rafId !== 0) { window.cancelAnimationFrame(rafId); rafId = 0; }
      if (timerId !== 0) { window.clearTimeout(timerId); timerId = 0; }
    };

    const schedule = (delayMs = 0) => {
      if (stopped || rafId !== 0 || timerId !== 0) return;
      if (delayMs > 16) {
        timerId = window.setTimeout(() => { timerId = 0; schedule(); }, delayMs);
        return;
      }
      rafId = window.requestAnimationFrame(loop);
    };

    const world = (now: number) => {
      const input = readWorld(displayWidth, now);
      if (!input) return null;
      return { ...measureSurfaces(input), favoured: input.favoured };
    };

    const beginPause = (now: number, restState: CompanionState = 'idle') => {
      if (phase !== 'pause') idleSince = now;
      phase = 'pause';
      pauseUntil = now + pauseDwellMs(daypartAt(wallClock()), rng);
      frames.set({ state: restState, direction: 0 });
    };

    const awaitWorld = (now: number) => {
      phase = 'pause';
      pauseUntil = now + PAUSE_POLL_MS;
    };

    const beginTransition = (via: CompanionState, target: CompanionState) => {
      phase = 'transition';
      transitionTarget = target;
      frames.set({
        state: via,
        onCycleEnd: () => {
          if (transitionTarget !== target) return;
          transitionTarget = null;
          asleep = target === 'sleeping';
          frames.set({ onCycleEnd: undefined });
          // A one-shot can finish before an interaction hands ownership back —
          // a drop re-entering a pose it was already in completes on the first
          // render pass, well inside the settle delay. Leaving phase on
          // 'transition' would strand the companion mid-air, since that phase
          // never consults gravity, so hand it to the pause case either way
          // and let whoever owns it take over from there.
          if (ownerRef.current !== 'autonomous') {
            phase = 'pause';
            pauseUntil = -Infinity;
            return;
          }
          if (target === 'sleeping') {
            phase = 'pause';
            pauseUntil = Infinity;
            frames.set({ state: 'sleeping' });
          } else {
            beginPause(performance.now());
          }
        },
      });
    };

    neutralizeRef.current = () => {
      phase = 'pause';
      pauseUntil = -Infinity;
      targetLedge = null;
      standingLedge = null;
      fallVelocity = 0;
      transitionTarget = null;
      asleep = false;
      // seenEdgeAt deliberately survives: it records which app events have
      // already been reacted to, which a change of owner does not undo.
      // Clearing it made every drag replay the last celebration.
      frames.set({ onCycleEnd: undefined });
    };

    transitionToRef.current = beginTransition;

    /**
     * The wall (if any) that would stop a step from `fromX` to `toX` at the
     * given feet height. A wall only blocks at the height it occupies, so a
     * card's side must not stop a companion walking along the floor far
     * below it.
     */
    const blockingWall = (
      fromX: number,
      toX: number,
      feetY: number,
      standing: Ledge | null,
    ): Wall | null => {
      for (const wall of walls) {
        // A surface's own edges are not barriers to whatever stands on it:
        // its top is walkable right up to them, and stepping off is how the
        // companion gets down. Treating them as walls pinned it to whatever
        // it was dropped on, since it starts every beat already at one. Only
        // the two edges this ledge was built from are exempt — a foreign wall
        // that happens to fall within its span still blocks.
        if (standing && wall.top === standing.y
          && (wall.x === standing.left || wall.x === standing.right + displayWidth)) continue;
        // A standing sprite's feet sit FEET_DROP_PX below the surface it
        // rests on (see restY), so the wall's own span needs the same
        // slack — otherwise a companion walking the floor a wall's surface
        // created would read as already past the wall's bottom edge.
        if (feetY < wall.top || feetY > wall.bottom + FEET_DROP_PX) continue;
        const crossed = fromX <= wall.x && toX >= wall.x
          ? wall.x
          : fromX >= wall.x && toX <= wall.x ? wall.x : null;
        if (crossed === null) continue;
        return wall;
      }
      return null;
    };

    const settle = (ledge: Ledge, now: number) => {
      frames.set({ y: restY(ledge, displayHeight) });
      fallVelocity = 0;
      targetLedge = null;
      beginPause(now);
    };

    const plan = (now: number) => {
      const measured = world(now);
      if (!measured) { awaitWorld(now); return; }

      ledges = measured.ledges;
      walls = measured.walls;
      const cur = frames.get();
      const clampedX = Math.min(ledges[0]!.right, Math.max(ledges[0]!.left, cur.x));
      if (clampedX !== cur.x) frames.set({ x: clampedX });

      const standing = resolveSurface(ledges, clampedX, cur.y, displayHeight);
      standingLedge = standing;

      if (Math.abs(cur.y - restY(standing, displayHeight)) > 1) {
        targetLedge = standing;
        if (restY(standing, displayHeight) < cur.y) {
          phase = 'jump';
          jumpFrom = cur.y;
          jumpElapsed = 0;
        } else {
          phase = 'fall';
          fallVelocity = 0;
        }
        frames.set({ state: 'falling' });
        return;
      }

      const standingRestY = restY(standing, displayHeight);
      const maxRise = MAX_HOP_RISE_FACTOR * displayHeight;
      const reachable = ledges.filter((l) => (
        l !== standing
        && overlapsX(standing, l)
        && standingRestY - restY(l, displayHeight) <= maxRise
      ));

      const route = routeHome({
        ledges,
        standing,
        x: clampedX,
        favoured: measured.favoured,
        petWidth: displayWidth,
        maxRise,
        restY: (l) => restY(l, displayHeight),
      });

      // Going home is the standing objective, so a route that has somewhere to
      // be overrides the idle chooser most of the time. Resting is still
      // allowed — the companion dawdles on the way rather than marching — but
      // not so often that it never arrives.
      const homeward = route.kind !== 'arrived' && rng() < HOMEWARD_COMMIT;
      const move = homeward
        ? (route.kind === 'hop' ? 'hop' : 'stroll')
        : chooseMove({
          canHop: reachable.length > 0,
          canClimb: false,
          favouredReachable: route.kind === 'hop',
          onFavoured: route.kind === 'arrived' && standing !== ledges[0],
        }, rng);

      if (move === 'rest') {
        const ambient = pickAmbient(rng, Object.keys(manifest.animations), daypartAt(wallClock()));
        beginPause(now, ambient ? AMBIENT_STATE[ambient] : 'idle');
        return;
      }

      if (move === 'hop' && reachable.length > 0) {
        // When the route says home is one hop away, usually take it.
        targetLedge = route.kind === 'hop' && rng() < FAVOURED_HOP_CHANCE
          ? route.ledge
          : reachable[Math.floor(rng() * reachable.length)] ?? reachable[0]!;
        // Only rising is a jump. Stepping down to a lower ledge is a fall, so
        // gravity carries it — arcing upward to reach something below reads as
        // a hop for no reason.
        if (restY(targetLedge, displayHeight) < cur.y) {
          phase = 'jump';
          jumpFrom = cur.y;
          jumpElapsed = 0;
        } else {
          phase = 'fall';
          fallVelocity = 0;
        }
        frames.set({ state: 'falling' });
        return;
      }

      // Follow the route when it has somewhere specific to be. A drop walks
      // deliberately off the edge — gravity takes over on the next beat, which
      // is how the companion climbs down off whatever it was dropped on.
      if (route.kind === 'walk' || route.kind === 'drop') {
        targetX = route.x;
      } else {
        targetX = pickStrollTarget(standing, clampedX, rng);
      }
      phase = 'walk';
      frames.set({ state: 'walking', direction: targetX >= clampedX ? 1 : -1 });
    };

    const loop = (now: number) => {
      rafId = 0;
      if (stopped) return;

      const dt = clampDt((now - last) / 1000);
      last = now;

      const owner = ownerRef.current;
      if (owner === 'autonomous' || owner === 'activity') {
        const reaction = decideReaction({
          activity: readCompanionActivity(),
          recording: isCapturingNow(),
          seenEdgeAt,
          lastReactionAt,
          now,
        });

        if (reaction.kind === 'sustained') {
          if (owner !== 'activity') {
            ownerRef.current = 'activity';
            setOwner('activity');
          }
          phase = 'pause';
          pauseUntil = Infinity;
          if (frames.get().state !== reaction.state) {
            frames.set({ state: reaction.state, direction: 0 });
          }
          schedule(PAUSE_POLL_MS);
          return;
        }

        if (owner === 'activity') {
          ownerRef.current = 'autonomous';
          setOwner('autonomous');
          idleSince = now;
          beginPause(now);
        }

        if (reaction.kind === 'edge') {
          seenEdgeAt = reaction.edgeAt;
          lastReactionAt = now;
          beginTransition(reaction.state, 'idle');
          schedule(PAUSE_POLL_MS);
          return;
        }
      }

      if (ownerRef.current !== 'autonomous') {
        schedule();
        return;
      }

      const cur = frames.get();

      switch (phase) {
        case 'pause': {
          if (asleep) break;
          if (now - idleSince >= LONG_IDLE_MS) {
            beginTransition('stretching', 'sleeping');
            break;
          }
          // The chrome moved while resting, so the ledge underfoot may be gone.
          // Re-plan now rather than at the end of a dwell that runs to 13s.
          if (layoutDirty) { layoutDirty = false; plan(now); break; }
          if (now >= pauseUntil) plan(now);
          break;
        }
        case 'transition': {
          break;
        }
        case 'walk': {
          const next = stepWalk(cur.x, targetX, speed, dt);
          const wall = blockingWall(cur.x, next.x, cur.y + displayHeight, standingLedge);
          if (wall) {
            frames.set({ x: wall.x });
            beginPause(now);
            break;
          }

          frames.set({ x: next.x });

          // Gravity applies while walking, not only at the next planning beat.
          // routeHome deliberately targets past a ledge's edge to climb down,
          // so this is the main descent path — without it the companion hangs
          // in the air until a dwell of up to 13s expires.
          const ground = resolveSurface(ledges, next.x, cur.y, displayHeight);
          if (restY(ground, displayHeight) > cur.y + SURFACE_EPS) {
            targetLedge = ground;
            fallVelocity = 0;
            phase = 'fall';
            frames.set({ state: 'falling' });
            break;
          }

          if (next.arrived) beginPause(now);
          break;
        }
        case 'fall': {
          if (!targetLedge) { beginPause(now); break; }
          const next = stepFall(cur.y, fallVelocity, dt);
          fallVelocity = next.velocity;
          const rest = restY(targetLedge, displayHeight);
          if (next.y >= rest) settle(targetLedge, now);
          else frames.set({ y: next.y });
          break;
        }
        case 'jump': {
          if (!targetLedge) { beginPause(now); break; }
          jumpElapsed += dt * 1000;
          const rest = restY(targetLedge, displayHeight);
          const next = stepJump(jumpFrom, rest, jumpElapsed);
          if (next.done) settle(targetLedge, now);
          else frames.set({ y: next.y });
          break;
        }
      }

      schedule(phase === 'pause' || phase === 'transition' ? PAUSE_POLL_MS : 0);
    };

    const onResize = () => {
      const measured = world(performance.now());
      if (!measured) return;
      const floor = measured.ledges[0]!;
      const cur = frames.get();
      frames.set({ x: Math.min(floor.right, Math.max(floor.left, cur.x)) });
      clearScheduled();
      last = performance.now();
      if (phase === 'fall' || phase === 'jump') {
        targetLedge = null;
        plan(last);
      }
      schedule();
    };

    let hiddenSince: number | null = null;

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = performance.now();
        return;
      }
      const away = hiddenSince === null ? 0 : performance.now() - hiddenSince;
      hiddenSince = null;
      if (away < AWAY_MS) return;
      if (ownerRef.current !== 'autonomous') return;
      last = performance.now();
      if (asleep) {
        asleep = false;
        beginTransition('wakingUp', 'idle');
        return;
      }
      beginTransition('peeking', 'idle');
    };

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    // Layout changes mark the discovered surfaces stale; the rescan itself
    // still waits for a planning beat, so a burst of mutations costs a flag,
    // not a burst of layout reads.
    // A pause can outlast the ledge it is standing on: app rows re-render and
    // panels reflow constantly, and recovery was gated on pauseDwellMs, which
    // runs to 13s. Measured in the running app: 1.9-4.4s stood on a ledge that
    // no longer existed. Bring the next plan forward to one poll instead.
    // The layer asks this on release: a drop in mid-air has to fall rather
    // than play the landing pose where the pointer let go.
    isGroundedRef.current = (x: number, y: number) => {
      // Stale is as wrong as empty: a release inside the poll window after a
      // layout change would otherwise resolve a ledge that has just gone and
      // land on it in mid-air.
      if (ledges.length === 0 || layoutDirty) {
        const measured = world(performance.now());
        if (!measured) return false;
        ledges = measured.ledges;
        walls = measured.walls;
        // layoutDirty is deliberately left set: the pause phase still owes a
        // re-plan, and consuming the flag here would silently cancel it.
      }
      const ground = resolveSurface(ledges, x, y, displayHeight);
      return Math.abs(restY(ground, displayHeight) - y) <= SURFACE_EPS + FEET_DROP_PX;
    };

    const onLayoutChanged = () => { layoutDirty = true; };
    const contentEl = document.querySelector(COMPANION_SLOTS.content);
    const stopWatchingLayout = contentEl ? watchLayout(contentEl, onLayoutChanged) : null;

    const stopDebug = import.meta.env.DEV
      ? exposeCompanionDebug({
        ledges: () => ledges,
        phase: () => phase,
        position: () => ({ x: frames.get().x, y: frames.get().y }),
      })
      : null;

    schedule();

    return () => {
      stopped = true;
      clearScheduled();
      window.removeEventListener('resize', onResize);
      stopWatchingLayout?.();
      stopDebug?.();
      document.removeEventListener('visibilitychange', onVisibility);
      neutralizeRef.current = null;
      clearSettleTimer();
    };
  }, [enabled, manifest, frames, displayWidth, displayHeight, rng, wallClock]);

  void owner;
  return controllerRef.current;
}
